import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GripVertical, Plus, Settings, BarChart3, Users, Calendar, Target } from 'lucide-react';

interface DashboardWidget {
  id: string;
  type: 'chart' | 'metric' | 'table' | 'progress';
  title: string;
  size: 'small' | 'medium' | 'large';
  data?: any;
}

export const DragDropDashboard: React.FC = () => {
  const [widgets, setWidgets] = useState<DashboardWidget[]>([
    {
      id: 'widget-1',
      type: 'metric',
      title: 'Project Health',
      size: 'small',
      data: { value: 87, trend: 'up' }
    },
    {
      id: 'widget-2',
      type: 'chart',
      title: 'Progress Over Time',
      size: 'large',
      data: { chartType: 'line' }
    },
    {
      id: 'widget-3',
      type: 'progress',
      title: 'Task Completion',
      size: 'medium',
      data: { completed: 72, total: 100 }
    },
    {
      id: 'widget-4',
      type: 'table',
      title: 'Recent Activities',
      size: 'medium',
      data: { rows: 5 }
    }
  ]);

  const [availableWidgets] = useState([
    { type: 'metric', title: 'Custom KPI', icon: Target },
    { type: 'chart', title: 'Analytics Chart', icon: BarChart3 },
    { type: 'table', title: 'Data Table', icon: Users },
    { type: 'progress', title: 'Progress Tracker', icon: Calendar }
  ]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWidgets(items);
  };

  const addWidget = (type: string, title: string) => {
    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      type: type as any,
      title,
      size: 'medium'
    };
    setWidgets([...widgets, newWidget]);
  };

  const removeWidget = (widgetId: string) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
  };

  const getWidgetIcon = (type: string) => {
    switch (type) {
      case 'metric': return <Target className="w-5 h-5" />;
      case 'chart': return <BarChart3 className="w-5 h-5" />;
      case 'table': return <Users className="w-5 h-5" />;
      case 'progress': return <Calendar className="w-5 h-5" />;
      default: return <Settings className="w-5 h-5" />;
    }
  };

  const getWidgetSize = (size: string) => {
    switch (size) {
      case 'small': return 'col-span-1';
      case 'medium': return 'col-span-2';
      case 'large': return 'col-span-3';
      default: return 'col-span-2';
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
            Custom Dashboard Builder
          </h2>
          <p className="text-gray-600 text-lg">
            Drag and drop widgets to create your perfect onboarding dashboard
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="dashboard" direction="horizontal">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="grid grid-cols-3 gap-6 min-h-[400px]"
                  >
                    {widgets.map((widget, index) => (
                      <Draggable key={widget.id} draggableId={widget.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`p-4 rounded-xl bg-white border border-gray-200 shadow-lg ${getWidgetSize(widget.size)} ${
                              snapshot.isDragging ? 'rotate-2 shadow-2xl' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-2">
                                {getWidgetIcon(widget.type)}
                                <h3 className="font-semibold text-gray-900">{widget.title}</h3>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div
                                  {...provided.dragHandleProps}
                                  className="text-gray-400 hover:text-gray-600 cursor-grab"
                                >
                                  <GripVertical className="w-4 h-4" />
                                </div>
                                <button
                                  onClick={() => removeWidget(widget.id)}
                                  className="text-gray-400 hover:text-red-600 text-sm"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                            
                            <div className="h-32 bg-gray-50 rounded-lg flex items-center justify-center">
                              <span className="text-gray-500 text-sm">
                                {widget.type.charAt(0).toUpperCase() + widget.type.slice(1)} Widget
                              </span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Plus className="w-5 h-5 text-[#4267A1] mr-2" />
                Add Widgets
              </h3>
              
              <div className="space-y-3">
                {availableWidgets.map((widget) => (
                  <button
                    key={widget.type}
                    onClick={() => addWidget(widget.type, widget.title)}
                    className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <widget.icon className="w-5 h-5 text-[#4267A1]" />
                    <span className="font-medium text-gray-900">{widget.title}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dashboard Actions</h3>
              <div className="space-y-3">
                <button className="w-full px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium">
                  Save Layout
                </button>
                <button className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium">
                  Reset to Default
                </button>
                <button className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium">
                  Export Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
